import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { usePremium } from '../contexts/PremiumContext';
import { useTranslation } from '../hooks/useTranslation';

// This function will be defined inside the component to access translations

const getStatusColor = (isoDate) => {
  if (!isoDate) return '#EF4444';
  
  const last = new Date(isoDate);
  
  // Check if date is valid
  if (isNaN(last.getTime())) {
    return '#EF4444';
  }
  
  const now = new Date();
  const diffDays = Math.floor((now - last) / (1000 * 60 * 60 * 24));
  if (diffDays <= 7) return '#10B981';
  if (diffDays <= 14) return '#F59E0B';
  return '#EF4444';
};

const getContactStatus = (isoDate) => {
  if (!isoDate) return 'urgent';
  
  const last = new Date(isoDate);
  
  // Check if date is valid
  if (isNaN(last.getTime())) {
    return 'urgent';
  }
  
  const now = new Date();
  const diffDays = Math.floor((now - last) / (1000 * 60 * 60 * 24));
  if (diffDays <= 7) return 'good';
  if (diffDays <= 14) return 'attention';
  return 'urgent';
};

const categorizeFriends = (friends) => {
  const good = [];
  const needsAction = [];
  
  friends.forEach(friend => {
    const status = getContactStatus(friend.lastContactDate || friend.lastContact);
    if (status === 'good') {
      good.push(friend);
    } else {
      needsAction.push(friend);
    }
  });
  
  return { good, needsAction };
};

const FriendSection = ({ title, friends, onFriendClick, emptyMessage, allFriends, formatLastConnected }) => {
  const { isFriendAccessible } = usePremium();
  const { t } = useTranslation();
  
  if (friends.length === 0) {
    return (
      <div className="friend-section">
        <h3 className="section-title">{title}</h3>
        <div className="simple-empty">{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className="friend-section">
      <h3 className="section-title">{title}</h3>
      <ul className="simple-friends-list">
        {friends.map(friend => {
          const isAccessible = isFriendAccessible(friend.id, allFriends);
          
          return (
            <li
              key={friend.id}
              className={`simple-friend-item ${!isAccessible ? 'friend-locked' : ''}`}
              onClick={() => isAccessible && onFriendClick && onFriendClick(friend)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (isAccessible) {
                    onFriendClick && onFriendClick(friend);
                  }
                }
              }}
              style={{ cursor: isAccessible ? 'pointer' : 'not-allowed' }}
            >
              <span
                className="status-dot"
                style={{ backgroundColor: getStatusColor(friend.lastContactDate || friend.lastContact) }}
              />
              <div className="friend-main">
                <div className="friend-name">{friend.name}</div>
                <div className="friend-meta">{t('lastConnected')} {formatLastConnected(friend.lastContactDate || friend.lastContact)}</div>
              </div>
              {!isAccessible && (
                <div className="friend-lock-overlay">
                  <div className="lock-icon">🔒</div>
                  <div className="lock-text">Premium</div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const SimpleFriendsList = ({ onFriendClick }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatLastConnected = (isoDate) => {
    if (!isoDate) return t('never');
    
    // Handle both lastContact and lastContactDate field names
    const dateValue = isoDate;
    const last = new Date(dateValue);
    
    // Check if date is valid
    if (isNaN(last.getTime())) {
      console.warn('Invalid date:', dateValue);
      return t('never');
    }
    
    const now = new Date();
    const diffDays = Math.floor((now - last) / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return t('today');
    if (diffDays === 1) return `1 ${t('dayAgo')}`;
    if (diffDays < 30) return `${diffDays} ${t('daysAgo')}`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) return t('moreThanMonthAgo');
    return t('moreThanMonthsAgo', { count: diffMonths });
  };

  useEffect(() => {
    const load = async () => {
      if (!currentUser) {
        setFriends([]);
        setLoading(false);
        return;
      }
      try {
        const q = query(
          collection(db, 'friends'),
          where('userId', '==', currentUser.uid)
        );
        const snap = await getDocs(q);
        const data = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const aTime = a.lastContactDate ? new Date(a.lastContactDate).getTime() : 0;
            const bTime = b.lastContactDate ? new Date(b.lastContactDate).getTime() : 0;
            return bTime - aTime;
          })
          .slice(0, 5);
        
        setFriends(data);
      } catch (e) {
        console.error('Failed to load friends', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  if (!currentUser) {
    return null;
  }

  return (
    <section className="simple-friends-section">
      <div className="simple-friends-card">
        <h2 className="simple-title">{t('friendshipOverview')}</h2>
        <p className="simple-subtitle">{t('quickGlance')}</p>
        
        {loading && (
          <div className="simple-empty">{t('loading')}</div>
        )}

        {!loading && friends.length === 0 && (
          <div className="simple-empty">{t('noFriendsYetTracker')}</div>
        )}

        {!loading && friends.length > 0 && (() => {
          const { good, needsAction } = categorizeFriends(friends);
          return (
            <>
              <FriendSection
                title={t('timeToTakeAction')}
                friends={needsAction}
                onFriendClick={onFriendClick}
                emptyMessage={t('allCaughtUp')}
                allFriends={friends}
                formatLastConnected={formatLastConnected}
              />
              <FriendSection
                title={t('allGoodCategory')}
                friends={good}
                onFriendClick={onFriendClick}
                emptyMessage={t('noFriendsInCategory')}
                allFriends={friends}
                formatLastConnected={formatLastConnected}
              />
            </>
          );
        })()}
      </div>
    </section>
  );
};

export default SimpleFriendsList;

